'use client';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, RegisterUserData } from '@/utils/authService';
import { useRouter } from 'next/navigation';
import { getCookie, setCookie, deleteCookie } from '@/utils/cookies';
// Define types
interface User {
  id: string;
  name: string;
  email?: string;
  roles?: string[];
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string, callbackUrl: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterUserData) => Promise<void>;
  isLoading: boolean;
  refreshToken: () => Promise<void>;
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: async () => {},
  register: async () => {},
  isLoading: false,
  refreshToken: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Debug logging
  useEffect(() => {
    console.log('AuthProvider mounted');
    console.log('Current cookies:', {
      token: getCookie('token'),
      refreshToken: getCookie('refresh_token'),
      tokenExpiry: getCookie('token_expiry'),
      username: getCookie('username'),
      userId: getCookie('user_id')
    });
  }, []);

  // Initial auth check
  useEffect(() => {
    const checkAuth = () => {
      const token = getCookie('token');
      const username = getCookie('username');
      const userId = getCookie('user_id');
      const tokenExpiry = getCookie('token_expiry');

      if (token && username && userId && tokenExpiry) {
        const expiryTime = Number(tokenExpiry);
        if (expiryTime > Date.now()) {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const roles = tokenPayload.realm_access?.roles || [];
          setUser({ id: userId, name: username, roles });
        } else {
          // Token is expired, let the refresh logic handle it
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const refreshTokenAndSetInterval = async () => {
      if (user) {
        await refreshToken(); // Initial refresh when user is set
        const interval = setInterval(refreshToken, 5 * 60 * 1000); // Refresh every 5 minutes
        return () => clearInterval(interval);
      }
    };

    refreshTokenAndSetInterval();
  }, [user]);

  const refreshToken = async () => {
    const refreshTokenCookie = getCookie('refresh_token');
    if (refreshTokenCookie) {
      try {
        const response = await authService.refreshToken(refreshTokenCookie);
        setCookie('token', response.access_token, response.expires_in);
        setCookie('refresh_token', response.refresh_token, response.expires_in * 2);
        setCookie('token_expiry', String(Date.now() + response.expires_in * 1000), response.expires_in);
        
        // Also renew username and user_id cookies to prevent them from expiring
        if (user?.name) {
          setCookie('username', user.name, response.expires_in);
        }
        if (user?.id) {
          setCookie('user_id', user.id, response.expires_in);
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
        logout();
      }
    } else {
      logout();
    }
  };

  const login = async (username: string, password: string, callbackUrl?: string) => {
    try {
      setIsLoading(true);
      console.log('Attempting login for:', username);
      
      const response = await authService.login(username, password);
      console.log('Login response:', response);

      const tokenPayload = JSON.parse(atob(response.access_token.split('.')[1]));
      console.log('Decoded token payload:', tokenPayload);
      const userId = tokenPayload.sub;
      const roles = tokenPayload.realm_access?.roles || [];

      // Set all necessary cookies
      setCookie('token', response.access_token, response.expires_in);
      setCookie('refresh_token', response.refresh_token, response.expires_in * 2);
      setCookie('token_expiry', String(Date.now() + response.expires_in * 1000), response.expires_in);
      setCookie('username', username, response.expires_in);
      setCookie('user_id', userId, response.expires_in);

      setUser({ id: userId, name: username, roles });

      console.log('Login successful, cookies set, roles:', roles);

      if (callbackUrl) {
        const basePath = "/";
        const fullCallbackUrl = callbackUrl.startsWith(basePath)
            ? callbackUrl
            : `${basePath}${callbackUrl}`;
        router.push(fullCallbackUrl);
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
        setIsLoading(true);
        console.log('Attempting logout via API route');

        await fetch('/api/auth/logout', { method: 'POST' });

        // The API route now handles cookie deletion. 
        // We just need to update the local state.
        setUser(null);
        
        console.log('Logout successful, client state cleared');
        // Optional: redirect to login page
        router.push('/login');
    } catch (error) {
        console.error('Logout failed:', error);
        // Fallback: clear state and cookies even if API call fails
        setUser(null);
        deleteCookie('token');
        deleteCookie('refresh_token');
        deleteCookie('token_expiry');
        deleteCookie('username');
        deleteCookie('user_id');
        router.push('/login');
    } finally {
        setIsLoading(false);
    }
  };

  const register = async (userData: RegisterUserData) => {
    try {
      setIsLoading(true);
      console.log(' Attempting registration for:', userData.username);

      await authService.register(userData);

      console.log('Registration successful');

    } catch (error) {
      console.error(' Registration failed:', error);


      throw error; // Rethrow to handle it elsewhere if needed
    } finally {
      setIsLoading(false);
    }
  };



  return (
      <AuthContext.Provider value={{ user, login, logout, register, isLoading, refreshToken }}>
        {children}
      </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
